import { SUBSCRIPTION_CANCELLED } from "constants/analyticsEvents";
import { Form, Formik } from "formik";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useCancelSubscriptionMutation } from "store/apis/subscriptions";
import { BackendError } from "types/utils/error";
import { object, string } from "yup";
import { Button } from "./DesignSystem/Button/Button";
import { MultiSelectFilter } from "./DesignSystem/Filter/Filter";
import Modal from "./DesignSystem/Modal/Modal";
import { TextArea } from "./DesignSystem/TextArea/TextArea";
import { Label } from "./DesignSystem/TextInput/TextInput";

const cancelReasonOptions = [
  {
    label: "Inadequate support/help",
    value: "Inadequate support/help",
  },
  {
    label: "Didn’t support my business needs",
    value: "Didn’t support my business needs",
  },
  {
    label: "Service wasn’t reliable during peak season",
    value: "Service wasn’t reliable during peak season",
  },
  {
    label: "Switched to a different tool/accountant",
    value: "Switched to a different tool/accountant",
  },
  {
    label: "Hidden fees or unclear billing",
    value: "Hidden fees or unclear billing",
  },
  {
    label: "Other",
    value: "Other",
  },
];

export const CancelSubscriptionConfirmation = ({
  isOpen,
  close,
  subscriptionId,
  handleCancelSubscription,
}: {
  isOpen: boolean;
  close: () => void;
  subscriptionId: string;
  handleCancelSubscription: () => void;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [cancelSubscription, { isLoading: isCancelling }] =
    useCancelSubscriptionMutation();
  const { alertToast, successToast } = useToast();
  const { trackEvent } = useAnalytics();

  const confirmCancelSubscription = async ({
    cancelReason,
    description,
  }: {
    cancelReason: string;
    description: string;
  }) => {
    let cancelReasons = cancelReason;

    if (description) {
      cancelReasons += ":" + description;
    }

    try {
      if (groupId) {
        await cancelSubscription({
          groupId,
          subscriptionId,
          entityId,
          cancelReason: cancelReasons,
        }).unwrap();
        successToast({
          message: "Subscription Cancelled Successfully",
          title: "Subscription Cancelled",
        });
      }
      trackEvent(SUBSCRIPTION_CANCELLED, {
        subscription_id: subscriptionId,
      });
      handleCancelSubscription();
    } catch (error: any) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    } finally {
      close();
    }
  };
  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Formik
        initialValues={{ cancelReason: "", description: "" }}
        onSubmit={confirmCancelSubscription}
        validationSchema={object({
          cancelReason: string().required("Please select a Reason"),
        })}
      >
        {({ values, setFieldValue, submitForm, validateForm, errors }) => (
          <Form>
            <Modal.Content size="regular">
              <Modal.Header>
                <Modal.Title>Cancel Subscription</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <Label required>
                  <span className="t-text-subtext t-text-text-60">
                    Why do you want to cancel?
                  </span>
                </Label>
                <div className="t-text-body-sm t-text-text-30 t-mb-4">
                  Help us make Inkle better with your feedback
                </div>

                <MultiSelectFilter
                  isSearchable={false}
                  isSelectAll={false}
                  onChange={(values) =>
                    setFieldValue("cancelReason", values.join(","))
                  }
                  checkboxSpace="small"
                  options={cancelReasonOptions}
                  selected={values.cancelReason.split(",").filter(Boolean)}
                />
                {errors.cancelReason && (
                  <div className="t-text-body-sm t-text-red t-mt-1">
                    Please select a reason
                  </div>
                )}
                <div className="t-mt-3">
                  <TextArea
                    label="Tell us more (Optional)"
                    name="description"
                  />
                </div>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button>Close</Button>
                </Modal.RawClose>
                <Button
                  customType="danger"
                  type="submit"
                  isLoading={isCancelling}
                  disabled={isCancelling}
                  onClick={() =>
                    validateForm().then((errors) => {
                      if (Object.keys(errors).length === 0) {
                        submitForm();
                      }
                    })
                  }
                >
                  Cancel subscription
                </Button>
              </Modal.FooterButtonGroup>
            </Modal.Content>
          </Form>
        )}
      </Formik>
    </Modal.Root>
  );
};
